/**
 * F-006 — Spawn API + simulation registry.
 *
 * SimulationRegistry tracks active planets, integrates the gravity well,
 * steps physics, and projects body state back into meshes each frame.
 * spawnPlanet is the single entry point everything else (slingshot in
 * F-011, planet queue in F-015, restart in F-019) calls.
 */

import { Scene } from "three";

import { createPlanet, type Planet, type Vec3Like } from "./planet.ts";
import {
  createCollisionRouter,
  type CollisionRouter,
  type MergeCandidate,
} from "./collisions.ts";
import { createPhysicsWorld, type PhysicsWorld } from "../physics/world.ts";
import {
  createGravityWell,
  type GravityWell,
  type GravityWellOptions,
} from "../physics/gravity-well.ts";

export interface SpawnPlanetOptions {
  tierIndex: number;
  position: Vec3Like;
  velocity?: Vec3Like;
}

export interface SimulationRegistry {
  planets: ReadonlyArray<Planet>;
  world: PhysicsWorld;
  well: GravityWell;
  collisions: CollisionRouter;
  spawnPlanet(opts: SpawnPlanetOptions): Planet;
  removePlanet(planet: Planet): void;
  step(dt: number): void;
  clear(): void;
  /** Drain pending merge candidates queued by the collision router. */
  drainMergeCandidates(): MergeCandidate[];
}

export interface CreateSimulationOptions {
  scene: Scene;
  well?: GravityWellOptions;
}

export function createSimulation(
  opts: CreateSimulationOptions,
): SimulationRegistry {
  const planets: Planet[] = [];
  const world = createPhysicsWorld();
  const well = createGravityWell(opts.well ?? {});
  const collisions = createCollisionRouter();

  const spawnPlanet = (sp: SpawnPlanetOptions): Planet => {
    const planet = createPlanet({
      tierIndex: sp.tierIndex,
      position: sp.position,
      ...(sp.velocity ? { velocity: sp.velocity } : {}),
    });
    opts.scene.add(planet.mesh);
    world.add(planet.body);
    planets.push(planet);
    collisions.observe(planet);
    return planet;
  };

  const removePlanet = (planet: Planet): void => {
    const idx = planets.indexOf(planet);
    if (idx === -1) return;
    planets.splice(idx, 1);
    collisions.forget(planet);
    world.remove(planet.body);
    planet.destroy();
  };

  const step = (dt: number): void => {
    well.applyToAll(world.bodies());
    world.step(dt);
    for (const p of planets) p.syncMeshFromBody();
  };

  const clear = (): void => {
    while (planets.length > 0) {
      const p = planets[planets.length - 1]!;
      removePlanet(p);
    }
  };

  const drainMergeCandidates = (): MergeCandidate[] => collisions.drain();

  return {
    planets,
    world,
    well,
    collisions,
    spawnPlanet,
    removePlanet,
    step,
    clear,
    drainMergeCandidates,
  };
}
